import { useLocation } from 'react-router-dom';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { supabase } from '@/lib/supabase';
import type {
  WorkspaceObjective,
  WorkspaceObjectiveInput,
  WorkspaceObjectiveMeasure,
  WorkspaceObjectiveMeasureInput,
} from '@shared/types/workspace-objectives';
import type { TenantMember } from '@shared/types/teams';
interface ObjectivesContextValue {
  objectives: WorkspaceObjective[];
  /** J3: measures by objective id (only measured objectives). */
  measures: Record<string, WorkspaceObjectiveMeasure>;
  members: TenantMember[];
  userId: string;
  isManager: boolean;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  /** Returns the objective id; an unchanged objective is not rewritten (a new
   * version would mark every concerned Thread stale). */
  save: (input: WorkspaceObjectiveInput, objective?: WorkspaceObjective) => Promise<string>;
  saveMeasure: (objectiveId: string, input: WorkspaceObjectiveMeasureInput | null) => Promise<void>;
  setStatus: (objective: WorkspaceObjective, status: WorkspaceObjective['status']) => Promise<void>;
}
const ObjectivesContext = createContext<ObjectivesContextValue | null>(null);
export const WorkspaceObjectivesProvider = ({ children }: { children: ReactNode }) => {
  const [objectives, setObjectives] = useState<WorkspaceObjective[]>([]);
  const [measures, setMeasures] = useState<Record<string, WorkspaceObjectiveMeasure>>({});
  const [members, setMembers] = useState<TenantMember[]>([]);
  const [userId, setUserId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (!user || authError) throw new Error('Votre session doit être renouvelée.');
      setUserId(user.id);
      const [goals, roster, measureRows] = await Promise.all([
        supabase.from('workspace_objectives').select('*').order('due_date', {
          ascending: true,
          nullsFirst: false,
        }).order('created_at'),
        supabase.from('tenant_members').select(
          'id,tenant_id,user_id,display_name,tenant_role,created_at,updated_at',
        ).order('display_name'),
        supabase.from('workspace_objective_measures').select('*'),
      ]);
      if (goals.error || roster.error || measureRows.error) {
        throw new Error('Les objectifs ne sont pas accessibles pour le moment. Réessayez.');
      }
      setObjectives((goals.data ?? []) as WorkspaceObjective[]);
      setMembers((roster.data ?? []) as TenantMember[]);
      setMeasures(Object.fromEntries(
        ((measureRows.data ?? []) as WorkspaceObjectiveMeasure[])
          .filter((row) => typeof row.objective_id === 'string')
          .map((row) => [row.objective_id, row]),
      ));
    } catch (cause) {
      setObjectives([]);
      setError(cause instanceof Error ? cause.message : 'Impossible de charger les objectifs.');
    } finally {
      setLoading(false);
    }
  }, []);
  const { pathname } = useLocation();
  const didLoad = useRef(false);
  useEffect(() => {
    if (!didLoad.current || pathname === '/profil') {
      didLoad.current = true;
      void refresh();
    }
  }, [pathname, refresh]);
  useEffect(() => {
    if (!userId) return undefined;
    let timer: number | null = null;
    const scheduleRefresh = () => {
      if (timer !== null) window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        timer = null;
        void refresh();
      }, 180);
    };
    const channel = supabase
      .channel(`workspace-objectives-${userId}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'workspace_objectives',
      }, scheduleRefresh)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'workspace_objective_measures',
      }, scheduleRefresh)
      .subscribe();
    return () => {
      if (timer !== null) window.clearTimeout(timer);
      void supabase.removeChannel(channel);
    };
  }, [refresh, userId]);
  const isManager = members.find((member) => member.user_id === userId)?.tenant_role === 'manager';
  const save = async (input: WorkspaceObjectiveInput, objective?: WorkspaceObjective) => {
    // Criteria the form does not edit (priority, target, baseline) are kept as they are.
    const values = {
      title: input.title.trim(),
      success_criteria: input.success_criteria.trim(),
      due_date: input.due_date || null,
      ...(input.priority ? { priority: input.priority } : {}),
      ...(input.target !== undefined ? { target: input.target?.trim() || null } : {}),
      ...(input.baseline !== undefined ? { baseline: input.baseline?.trim() || null } : {}),
      ...(input.parent_objective_id !== undefined ? { parent_objective_id: input.parent_objective_id } : {}),
    };
    if (
      objective && values.title === objective.title &&
      values.success_criteria === objective.success_criteria &&
      values.due_date === (objective.due_date ?? null) &&
      (values.priority ?? objective.priority) === objective.priority &&
      (values.target === undefined || values.target === objective.target) &&
      (values.baseline === undefined || values.baseline === objective.baseline) &&
      (values.parent_objective_id === undefined || values.parent_objective_id === objective.parent_objective_id)
    ) return objective.id;
    const query = objective
      ? supabase.from('workspace_objectives').update(values).eq('id', objective.id).eq(
        'updated_at',
        objective.updated_at,
      )
      : supabase.from('workspace_objectives').insert({
        ...values,
        scope: input.scope,
        owner_user_id: input.scope === 'company' ? null : input.owner_user_id,
      });
    const { data, error: saveError } = await query.select('id').single();
    if (saveError || !data) {
      throw new Error(
        'L’objectif n’a pas été enregistré. Vérifiez vos droits ou rechargez la dernière version.',
      );
    }
    await refresh();
    return String(data.id);
  };
  const saveMeasure = async (objectiveId: string, input: WorkspaceObjectiveMeasureInput | null) => {
    const current = measures[objectiveId];
    if (!input && !current) return;
    if (
      input && current &&
      (Object.keys(input) as Array<keyof WorkspaceObjectiveMeasureInput>).every((key) => current[key] === input[key])
    ) return;
    const { error: measureError } = input
      ? await supabase.from('workspace_objective_measures')
        .upsert({ objective_id: objectiveId, ...input }, { onConflict: 'objective_id' })
      : await supabase.from('workspace_objective_measures').delete().eq('objective_id', objectiveId);
    if (measureError) {
      throw new Error(
        measureError.message.includes('objective_measure_status_invalid')
          ? 'Ce statut n’existe pas dans la liste choisie.'
          : measureError.message.includes('objective_measure_app_forbidden')
          ? 'Vous n’avez pas accès à l’application de cette liste.'
          : measureError.message.includes('objective_measure_hubspot_unavailable')
          ? 'HubSpot n’est pas connecté pour l’entreprise.'
          : 'La mesure n’a pas été enregistrée. Vérifiez vos droits.',
      );
    }
    await refresh();
  };
  const setStatus = async (objective: WorkspaceObjective, status: WorkspaceObjective['status']) => {
    const { data, error: saveError } = await supabase.from('workspace_objectives').update({
      status,
    }).eq('id', objective.id).eq('updated_at', objective.updated_at).select('id').single();
    if (saveError || !data) {
      throw new Error('Le statut n’a pas été enregistré. Rechargez la dernière version.');
    }
    await refresh();
  };
  const value = useMemo(
    () => ({
      objectives, measures, members, userId, isManager, loading, error, refresh, save, saveMeasure, setStatus,
    }),
    [objectives, measures, members, userId, isManager, loading, error, refresh],
  );
  return <ObjectivesContext.Provider value={value}>{children}</ObjectivesContext.Provider>;
};
export const useWorkspaceObjectives = () => {
  const context = useContext(ObjectivesContext);
  if (!context) throw new Error('WorkspaceObjectivesProvider is required.');
  return context;
};

export const useOptionalWorkspaceObjectives = () => useContext(ObjectivesContext);
